import styles from "./Widgets.module.css";
import { formatMNOK } from "../../format";
import { useCachedResource } from "../../lib/swrCache";
import { fetchProjectKpi, currentQuarter } from "../../lib/report";
import { fraBizview } from "../../lib/prosjekter";

type ProsjektRad = { projectId: number; projectName: string };

async function hentProsjekter(): Promise<ProsjektRad[]> {
  const r = await fetch("/api/projects");
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return fraBizview((await r.json()) as ProsjektRad[]);
}

export function ProsjektKpiWidget({
  projektId,
  onVelg,
}: {
  projektId?: number;
  onVelg: (id: number) => void;
}) {
  const { data: prosjekter } = useCachedResource("projects:all", hentProsjekter, {
    persist: true,
  });
  const liste = [...(prosjekter ?? [])].sort((a, b) =>
    a.projectName.localeCompare(b.projectName, "nb")
  );

  return (
    <div className={styles.prosjektKpi}>
      <select
        className={styles.velgProsjekt}
        value={projektId ?? ""}
        onChange={(e) => e.target.value && onVelg(Number(e.target.value))}
        aria-label="Velg prosjekt"
      >
        <option value="">Velg prosjekt…</option>
        {liste.map((p) => (
          <option key={p.projectId} value={p.projectId}>
            {p.projectName}
          </option>
        ))}
      </select>
      {projektId ? (
        <KpiInnhold projektId={projektId} />
      ) : (
        <p className={styles.muted}>Velg et prosjekt for å se nøkkeltall.</p>
      )}
    </div>
  );
}

function KpiInnhold({ projektId }: { projektId: number }) {
  const { year, q } = currentQuarter();
  const { data, feil } = useCachedResource(
    `projectKpi:${projektId}:${year}:${q}`,
    () => fetchProjectKpi(projektId, year, q),
    { persist: true }
  );

  if (feil && !data) return <p className={styles.muted}>Kunne ikke laste nøkkeltall.</p>;
  if (!data) return <p className={styles.muted}>Laster…</p>;

  const p = data.project;
  const f = p.fremdrift;
  const pct = (v: number | null) => (v == null ? "—" : `${Math.round(v * 100)} %`);
  return (
    <div className={styles.pkInnhold}>
      <div className={styles.statuslinje}>
        {p.status && <span className={styles.statusPill}>{p.status}</span>}
        <span className={styles.pkKvartal}>{data.quarter.label}</span>
      </div>

      <div className={styles.kpiRad2}>
        <div className={`${styles.kpi} ${styles.kpiSm} ${styles.kpiAccent}`}>
          <span className={styles.kpiVerdi}>{formatMNOK(p.resultat)}</span>
          <span className={styles.kpiLabel}>Resultat (MNOK)</span>
        </div>
        <Kpi label="Margin" verdi={pct(p.margin)} />
        <Kpi label="GB-andel (MNOK)" verdi={p.gbAndel == null ? "—" : formatMNOK(p.gbAndel)} />
        <Kpi label="Solgt verdi (MNOK)" verdi={formatMNOK(p.salg.verdiAkk)} />
      </div>

      <Bar
        label="Forbruk"
        verdi={p.kostAkk}
        maks={p.budKost}
        tekst={`${formatMNOK(p.kostAkk)} / ${formatMNOK(p.budKost)}`}
      />
      <Bar
        label="Salg"
        verdi={p.salg.solgtAkk}
        maks={p.salg.total}
        tekst={`${p.salg.solgtAkk}/${p.salg.total}${
          p.salg.salgsgrad != null ? ` · ${Math.round(p.salg.salgsgrad * 100)} %` : ""
        }`}
      />

      <div className={styles.fremdrift}>
        Milepæler {f.milepaelerNadd}/{f.milepaelerTotal}
        {f.milepaelerForsinket > 0 ? ` · ${f.milepaelerForsinket} forsinket` : ""}
        {` · ${f.aktiviteterFullfortKvartal} aktiviteter fullført i kvartalet`}
      </div>
    </div>
  );
}

function Kpi({ label, verdi }: { label: string; verdi: string }) {
  return (
    <div className={`${styles.kpi} ${styles.kpiSm}`}>
      <span className={styles.kpiVerdi}>{verdi}</span>
      <span className={styles.kpiLabel}>{label}</span>
    </div>
  );
}

function Bar({
  label,
  verdi,
  maks,
  tekst,
}: {
  label: string;
  verdi: number;
  maks: number;
  tekst: string;
}) {
  const andel = maks > 0 ? verdi / maks : 0;
  const bredde = Math.min(100, Math.round(andel * 100));
  const over = andel > 1;
  return (
    <div className={styles.bar}>
      <div className={styles.barTopp}>
        <span>{label}</span>
        <span className={styles.barTekst}>{tekst}</span>
      </div>
      <div className={styles.barSpor}>
        <div
          className={`${styles.barFyll} ${over ? styles.barFyllOver : ""}`}
          style={{ width: `${over ? 100 : bredde}%` }}
        />
      </div>
    </div>
  );
}
